import React, { useEffect, useMemo } from 'react';
import _ from 'lodash';
import { useContextSelector } from 'use-context-selector';

import styles from './Question.module.scss';
import Feedback from '../../../UI/Feedback/Feedback';
import Label from '../../../context/Label/Label';
import MultipleChoice from './MultipleChoice/MultipleChoice';
import Essay from './Essay/Essay';
import FillInTheBlanks from './FillInTheBlanks/FillInTheBlanks';
import MatchingWrapper from './Matching/MatchingWrapper';
import { UIContext } from '../../../context/UI/UIContext';
import OrderingWrapper from './Ordering/OrderingWrapper';

function Question({
  question = {},
  tracking,
  config = {},
  onChange,
  cur,
  max,
  globalState,
  className,
}) {
  const { course } = globalState;
  const { primary } = globalState.getColorScheme();
  const { answeredCorrectly, submitted } = tracking;
  const setBackground = useContextSelector(UIContext, (c) => c.setBackground);
  const clearBackground = useContextSelector(
    UIContext,
    (c) => c.clearBackground
  );

  useEffect(() => {
    const hasQuizBackground = !_.isEmpty(config?.background);

    // first, check for question background
    if (!_.isEmpty(question.background) && !config?.showAllQuestionsInPage) {
      setBackground(question.background);
    } else {
      // then, check for quiz background (to restore it)
      hasQuizBackground && setBackground(config.background);
    }

    return () => {
      if (!hasQuizBackground) {
        clearBackground();
      }
    };
  }, [question.background, config.background, config.showAllQuestionsInPage]); // eslint-disable-line

  const _className = [styles.Question];
  className && _className.push(className);

  const renderedQuestionType = useMemo(() => {
    switch (question.resourceType) {
      case 'essay':
        return (
          <Essay
            question={question}
            tracking={tracking}
            config={config}
            onChange={onChange}
          />
        );
      case 'fillInTheBlank':
        return (
          <FillInTheBlanks
            question={question}
            tracking={tracking}
            config={config}
            onChange={onChange}
          />
        );
      case 'matching':
        return (
          <MatchingWrapper
            question={question}
            tracking={tracking}
            config={config}
            onChange={onChange}
          />
        );
      case 'ordering':
        return (
          <OrderingWrapper
            question={question}
            tracking={tracking}
            config={config}
            onChange={onChange}
          />
        );
      case 'question':
      case 'multipleChoice':
        return (
          <MultipleChoice
            question={question}
            tracking={tracking}
            config={config}
            onChange={onChange}
          />
        );
      default:
        return null;
    }
  }, [config, tracking, question, onChange]);

  return (
    <div className={_className.join(' ')}>
      <div className={styles.Question__counter}>
        <span style={{ color: primary }}>
          <Label>question</Label> {cur}/{max}
        </span>
      </div>
      {question?.textImage?.image?.name && course?.baseURL && (
        <div
          className={styles.Question__image}
          style={{
            backgroundImage: `url('${course?.baseURL}/${question?.textImage?.image?.name}')`,
            ...question?.textImage?.style,
          }}
        />
      )}
      <div className={styles.Question__question}>{question.text}</div>
      {renderedQuestionType}
      {!!submitted &&
        ((!_.isEmpty(question?.feedback?.whenCorrect) &&
          answeredCorrectly === true) ||
          (!_.isEmpty(question?.feedback?.whenIncorrect) &&
            answeredCorrectly === false)) && (
          <Feedback
            incorrect={answeredCorrectly === false}
            correct={answeredCorrectly === true}
            className={styles.Question__feedback}
          >
            {answeredCorrectly
              ? question?.feedback?.whenCorrect
              : question?.feedback?.whenIncorrect}
          </Feedback>
        )}
    </div>
  );
}

export default Question;
